import { IDocumentTokenDto, DocumentTokenDto } from "@/models/document-token.entity";
import { DocumentTokenCoordinates } from "@/models/document-token-coordinates.entity";
import { IHistory, TemplateHistoryAction } from "./token-history";

export const FONT_SIZE_MIN = 4;
export const FONT_SIZE_MAX = 72;
export const FONT_SIZE_DEFAULT = 12;

/** Convert font size in pt to mm (exact: 1pt = 25.4/72 mm) */
export const PT_TO_MM = 25.4 / 72;

export interface ITokenEntity {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  isRequired: boolean;
  isEditable: boolean;
  title?: string;
  description?: string;
  value?: string;
  fontSize?: number;
}

let _idCounter = 0;
function generateId(): string {
  return `token-${Date.now()}-${++_idCounter}`;
}

export type TokenEntityCtor<T extends BaseTokenEntity = BaseTokenEntity> = new (
  data: Partial<ITokenEntity> & { x: number; y: number; w?: number; h?: number },
  history?: IHistory
) => T;

export abstract class BaseTokenEntity implements ITokenEntity {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  isRequired: boolean;
  isEditable: boolean;
  title?: string;
  description?: string;
  value?: string;
  fontSize?: number;

  private _history: IHistory | null = null;

  abstract get tokenString(): string;
  abstract get colorHex(): string;
  abstract get labelKey(): string;
  abstract get icon(): string;
  /** Vuetify color string, e.g. "blue-grey darken-1" */
  abstract get color(): string;

  get token(): string {
    return this.tokenString;
  }

  get fingerprint(): string {
    return [
      this.id,
      this.x,
      this.y,
      this.w,
      this.h,
      this.isRequired,
      this.isEditable,
      this.title ?? "",
      this.description ?? "",
      this.value ?? "",
      this.fontSize ?? "",
      this.tokenString
    ].join(",");
  }

  get effectiveFontSize(): number {
    return this.fontSize ?? 12;
  }

  abstract get defaultSize(): { w: number; h: number };
  abstract get fixedSize(): boolean;
  abstract validateConstraints(pageWidthMm?: number, pageHeightMm?: number): void;
  abstract toDto(widthPx: number, heightPx: number): IDocumentTokenDto;

  constructor(data: Partial<ITokenEntity> & { x: number; y: number; w?: number; h?: number }, history?: IHistory) {
    this._history = history ?? null;
    this.id = data.id ?? generateId();
    this.x = data.x;
    this.y = data.y;
    this.w = data.w ?? this.defaultSize.w;
    this.h = data.h ?? this.defaultSize.h;
    this.isRequired = data.isRequired ?? false;
    this.isEditable = data.isEditable ?? true;
    this.title = data.title;
    this.description = data.description;
    this.value = data.value;
    this.fontSize = data.fontSize;
  }

  updateFontSize(size: number): void {
    this.fontSize = size;
    this._history?.push(TemplateHistoryAction.UPDATE_TOKEN, "Font size changed");
  }

  updateValue(val: string): void {
    this.value = val;
    this._history?.push(TemplateHistoryAction.UPDATE_TOKEN, "Value changed");
  }

  updatePosition(x: number, y: number, w: number, h: number): void {
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
    this._history?.push(TemplateHistoryAction.MOVE_TOKEN, "Position changed");
  }

  updateFields(patch: Partial<ITokenEntity>): void {
    Object.assign(this, patch);
    if (!this.isEditable) {
      this.isRequired = false;
    }
    this._history?.push(TemplateHistoryAction.UPDATE_TOKEN, "Fields updated");
  }

  clone(history?: IHistory): BaseTokenEntity {
    const ctor = this.constructor as TokenEntityCtor;
    return new ctor({ ...(this as unknown as Partial<ITokenEntity>), x: this.x, y: this.y }, history);
  }

  cloneWithOffset(history?: IHistory): BaseTokenEntity {
    const copy = this.clone(history);
    copy.id = generateId();
    copy.x = Math.min(1 - copy.w, copy.x + 0.02);
    copy.y = Math.min(1 - copy.h, copy.y + 0.02);
    return copy;
  }

  adjustFontSize(delta: number): void {
    const next = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, (this.fontSize ?? FONT_SIZE_DEFAULT) + delta));
    this.updateFields({ fontSize: next });
  }

  protected buildDto(widthMm: number, heightMm: number, fontSize = 12): IDocumentTokenDto {
    return new DocumentTokenDto({
      token: this.tokenString,
      title: this.title,
      description: this.description,
      isRequired: this.isRequired,
      isEditable: this.isEditable,
      value: this.value,
      coordinates: new DocumentTokenCoordinates({
        x: this.x * widthMm,
        y: this.y * heightMm,
        w: this.w * widthMm,
        h: this.h * heightMm,
        fontSize
      })
    });
  }

  equals(other: BaseTokenEntity): boolean {
    return (
      this.id === other.id &&
      this.constructor.name === other.constructor.name &&
      this.x === other.x &&
      this.y === other.y &&
      this.w === other.w &&
      this.h === other.h &&
      this.isRequired === other.isRequired &&
      this.isEditable === other.isEditable &&
      (this.title ?? "") === (other.title ?? "") &&
      (this.description ?? "") === (other.description ?? "") &&
      (this.value ?? "") === (other.value ?? "") &&
      (this.fontSize ?? "") === (other.fontSize ?? "")
    );
  }

  protected centerBounds(nextWidth: number, nextHeight: number) {
    const cx = this.x + this.w / 2;
    const cy = this.y + this.h / 2;
    this.w = nextWidth;
    this.h = nextHeight;
    this.x = cx - nextWidth / 2;
    this.y = cy - nextHeight / 2;
  }
}
